<template>
  <Flex
    direction="column"
    fullWidth
  >
    <Flex
      direction="column"
      :gap="0.25"
    >
      <BaseHeading size="l">
        {{ $t('admin.treatwell.pages.dashboard.heading') }}
      </BaseHeading>
      <BaseText color="secondary">
        {{ $t('admin.treatwell.pages.dashboard.subheading') }}
      </BaseText>
    </Flex>
    <BaseCard
      :heading="$t('admin.treatwell.pages.dashboard.your_page')"
      v-test="'tw-dashboard-status'"
    >
      <template #header>
        <BaseText
          v-if="hasActiveVenue && treatwellVenue?.url"
          :href="treatwellVenue.url"
          v-test="'tw-venue-page'"
        >
          {{ $t('admin.treatwell.pages.dashboard.view_page') }}
        </BaseText>
      </template>
      <Flex
        justify="between"
        align="center"
        wrap
      >
        <StatusLabels />
        <Flex
          align="center"
          wrap
        >
          <BaseText
            v-if="treatwellStatus.contractSigned && contractDownloadUrl"
            :href="contractDownloadUrl"
            iconBefore="download"
            v-test="'tw-contract-download'"
          >
            {{ $t('admin.treatwell.pages.dashboard.contract_download') }}
          </BaseText>
          <BaseText
            v-if="showChurn"
            color="warning"
            iconBefore="delete"
            @click="emit('cancel')"
            v-test="'tw-churn-button'"
          >
            {{ $t('treatwell.churn') }}
          </BaseText>
        </Flex>
      </Flex>
    </BaseCard>
    <BaseCard
      v-if="!completedOnboardingTasks"
      :heading="$t('admin.treatwell.pages.dashboard.setup_heading')"
      v-test="'tw-setup-tasks'"
    >
      <Flex
        direction="column"
        :gap="1"
      >
        <BaseText color="secondary">
          {{ $t('admin.treatwell.main.getting_paid.description') }}
        </BaseText>
        <div :class="$style.links">
          <BaseText
            v-if="!hasFeatureFlag('treatwell-brand')"
            :href="$t('treatwell.landing.link_video.url')"
            iconBefore="play-circle"
          >
            {{ $t('treatwell.landing.link_video.text') }}
          </BaseText>
          <BaseText
            :href="helpCenterArticleUrl($t('treatwell.landing.link_conditions.url'))"
            iconBefore="book-open"
          >
            {{ $t('treatwell.landing.link_conditions.text') }}
          </BaseText>
        </div>
        <SetupTasks
          :treatwellVenue="treatwellVenue"
          :contractDownloadUrl="contractDownloadUrl"
          @kyc="emit('kyc')"
          @subscribe="emit('subscribe')"
        />
      </Flex>
    </BaseCard>
    <template v-if="completedOnboardingTasks || hasActiveVenue">
      <BaseCard
        v-if="!allStepsDone"
        :heading="$t('admin.treatwell.pages.dashboard.next_steps.heading')"
        v-test="'tw-next-steps'"
      >
        <Flex
          direction="column"
          :gap="0.5"
        >
          <BaseTask
            v-if="isTrial"
            :heading="$t('subscription.heading', brandName())"
            text=""
            state="required"
            buttonIcon="external_link"
            @click="emit('subscribe')"
            @buttonClick="emit('subscribe')"
            v-test="'subscribe-todo-item'"
            v-intercom="'mp-dashboard-task-subscribe'"
          />
          <BaseTask
            :heading="$t('admin.treatwell.pages.dashboard.next_steps.profile')"
            :text="$t('admin.treatwell.pages.dashboard.next_steps.profile_text')"
            :state="profileDone ? 'completed' : undefined"
            buttonIcon="arrow-right"
            @click="emit('open', 'profile')"
            @buttonClick="emit('open', 'profile')"
            v-test="'tw-next-step-profile'"
          />
          <BaseTask
            :heading="$t('admin.treatwell.pages.dashboard.next_steps.reviews')"
            :text="$t('admin.treatwell.pages.dashboard.next_steps.reviews_text')"
            :state="reviewsDone ? 'completed' : undefined"
            buttonIcon="arrow-right"
            @click="emit('open', 'reviews')"
            @buttonClick="emit('open', 'reviews')"
            v-test="'tw-next-step-reviews'"
          />
        </Flex>
      </BaseCard>
      <LastMinuteDiscountCard />
      <Stats />
    </template>
  </Flex>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { brandName } from '@/helpers/brand-name';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { useCompanyStore } from '@/stores/company';
import { useTreatwellStore } from '@/stores/treatwell';
import { useUserStore } from '@/stores/user';
import type { TreatwellVenue } from '@/types';
import Stats from '../Stats.vue';
import StatusLabels from '../StatusLabels.vue';
import SetupTasks from '../tasks/SetupTasks.vue';
import { useProfileTasks } from '../tasks/use-profile-tasks';
import LastMinuteDiscountCard from './LastMinuteDiscountCard.vue';
import type { DashboardSection } from './types';

// Until the required setup is done this is the setup checklist; after that it shows the
// stats, the contract, the venue page and what to do next
const props = defineProps<{
  treatwellVenue: Partial<TreatwellVenue> | null;
  contractDownloadUrl?: string | null;
}>();

const emit = defineEmits<{
  kyc: [];
  subscribe: [];
  cancel: [];
  open: [section: DashboardSection];
}>();

const { treatwellStatus, hasActiveVenue, completedOnboardingTasks } =
  storeToRefs(useTreatwellStore());
const { company, companySettings } = storeToRefs(useCompanyStore());
const { isTrial } = useCompanyStore();
const { hasFeatureFlag } = useUserStore();

const showChurn = computed(
  () =>
    treatwellStatus.value.hasVenue &&
    !treatwellStatus.value.kycStatus?.kycCompleted &&
    treatwellStatus.value.freeCommissionRemainingDays > 0 &&
    company.value.activeSubscription?.plan !== 'lite',
);

const { profileDone } = useProfileTasks(() => props.treatwellVenue);
const reviewsDone = computed(
  () =>
    companySettings.value.customers.appointmentFeedback &&
    treatwellStatus.value.reviewsPublished,
);
// Nothing left to suggest once the profile and reviews are set up
const allStepsDone = computed(
  () => !isTrial && profileDone.value && reviewsDone.value,
);
</script>

<style
  lang="scss"
  module
>
.links {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing;
}
</style>
